import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Users as UsersIcon, Loader2, Search, Check, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useAuth,
  Card,
  Button,
  Badge,
  appSeatService,
  toast,
} from '@aaas/shared';
import { useWorkspaceSubscriptions } from '../hooks/useWorkspaceSubscriptions';
import { useWorkspaceMembers } from '../hooks/useWorkspaceMembers';
import { useAllAppSeats } from '../hooks/useAllAppSeats';

export function AssignSeatsPage() {
  const { currentWorkspace, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('portal');

  const appId = searchParams.get('app');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: subscriptions, isLoading: subscriptionsLoading } = useWorkspaceSubscriptions(
    currentWorkspace?.id
  );

  const subscription = subscriptions?.find((sub) => sub.app_id === appId);

  const { data: members, isLoading: membersLoading } = useWorkspaceMembers(currentWorkspace?.id, {
    excludeOwner: true,
  });

  const {
    data: assignedSet,
    isLoading: seatsLoading,
    refetch: refetchSeats,
  } = useAllAppSeats(currentWorkspace?.id, appId);

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    if (!debouncedSearch) return members;
    const q = debouncedSearch.toLowerCase();
    return members.filter(
      (m) =>
        (m.display_name && m.display_name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q))
    );
  }, [members, debouncedSearch]);

  const handleToggleSeat = async (accountId: string) => {
    if (!currentWorkspace || !appId || !user || !assignedSet) return;

    setUpdatingId(accountId);
    try {
      const newSet = new Set(assignedSet);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }

      const idToken = await user.getIdToken();
      await appSeatService.bulkUpdateSeats(
        currentWorkspace.id,
        appId,
        Array.from(newSet),
        idToken
      );

      await refetchSeats();
      toast.success(
        newSet.has(accountId)
          ? t('assignSeats.toasts.seatAssigned')
          : t('assignSeats.toasts.seatRemoved'),
      );
    } catch (error) {
      console.error('Failed to update seat:', error);
      toast.error(t('assignSeats.toasts.updateFailed'));
    } finally {
      setUpdatingId(null);
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('assignSeats.noWorkspace')}</h1>
          <p className="text-gray-600">{t('assignSeats.selectWorkspace')}</p>
        </div>
      </div>
    );
  }

  if (!appId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('assignSeats.noAppSelected')}</h1>
          <p className="text-gray-600 mt-2">{t('assignSeats.selectApp')}</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            {t('assignSeats.backToApps')}
          </Button>
        </div>
      </div>
    );
  }

  const isLoading = subscriptionsLoading || membersLoading || seatsLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center space-x-3 mb-2">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('assignSeats.heading')}</h1>
                <p className="text-sm text-gray-600 mt-1">{t('assignSeats.subheading')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : !subscription ? (
          <Card className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('assignSeats.appNotFound')}</h3>
              <p className="text-gray-600 mb-6">{t('assignSeats.appNotFoundText')}</p>
              <Button onClick={() => navigate('/')} className="flex items-center gap-2 mx-auto">
                <ArrowLeft className="w-4 h-4" />
                {t('assignSeats.goToApps')}
              </Button>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="p-6">
              {/* App info */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                    {subscription.app_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {subscription.app_name}
                      </h2>
                      {subscription.app_is_beta && (
                        <Badge
                          variant="outline"
                          className="text-xs border-orange-300 text-orange-700 bg-orange-50"
                        >
                          {t('betaTag', { defaultValue: 'Beta' })}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">@{subscription.app_slug}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-2">{t('assignSeats.hint')}</p>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('assignSeats.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Member grid */}
              {filteredMembers.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 border border-gray-200 rounded-lg">
                  {t('assignSeats.noMembers')}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredMembers.map((member) => {
                    const isAssigned = assignedSet?.has(member.account_id) ?? false;
                    const isUpdating = updatingId === member.account_id;
                    return (
                      <button
                        key={member.account_id}
                        type="button"
                        onClick={() => !isUpdating && handleToggleSeat(member.account_id)}
                        disabled={isUpdating}
                        className={[
                          'flex items-center gap-3 p-3 rounded-lg border text-left transition-all',
                          isAssigned
                            ? 'border-l-4 border-l-green-500 bg-green-50/50 border-green-200'
                            : 'border border-gray-200 bg-white',
                          !isUpdating
                            ? 'hover:shadow-sm hover:border-gray-300 cursor-pointer'
                            : 'cursor-default opacity-70',
                        ].join(' ')}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-medium text-xs">
                            {member.display_name?.charAt(0).toUpperCase() ||
                              member.email?.charAt(0).toUpperCase() ||
                              '?'}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {member.display_name || member.email}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{member.email}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {isUpdating ? (
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                          ) : isAssigned ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <Circle className="h-5 w-5 text-gray-300" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
